import type {
  NtCourse, NtStudent, NtProposal, NtPlacement, NtPlacementRow, NtBlock, NtRoster, NtOverride, NtSchedule,
} from '../../core/types'

/**
 * Placement — the part of NorseTerm that would otherwise be a weekend with a
 * spreadsheet.
 *
 * Ported from the prototype deliberately unchanged in behaviour, because the
 * behaviour was already right and it is the piece a principal will check by
 * hand. What changed is that it is now a pure function: courses and students
 * in, a result out, no DOM and no global state. That makes it testable, and
 * this is exactly the code that has to be testable — the failure mode is not a
 * crash, it is thirty kids quietly in the wrong room on Monday.
 *
 * The algorithm, in order:
 *
 *   1. Who picks first. The district's rule is first come, first served —
 *      the ranking form's timestamp (`first-come`). Seniors-first and a
 *      reproducible lottery are the alternatives. A values decision, not a
 *      technical one, so it lives on the term (`rules.placementPriority`).
 *   2. An approved independent project takes the student out entirely — they
 *      are placed in neither block. It beats a hand placement too.
 *   2½. A student placed by hand (an NtOverride) stays where they were put, in
 *      that block, every run. Their seat is reserved before anyone else is
 *      placed — even past the cap, because somebody chose it on purpose — and
 *      balancing never moves them. A hand placement into a course that is not
 *      on offer, or not in that block, is ignored rather than trusted.
 *   3. Walk their ranked list in order. Take the first AM course with a seat,
 *      and the first PM course with a seat. Honouring rank order is the whole
 *      promise the form makes to a student.
 *   4. Anyone still unplaced gets filled into the emptiest course in that
 *      block. Emptiest, not first — it spreads the unlucky rather than
 *      stacking them.
 *
 * Then `balanceRosters` is a separate, deliberate second step: it moves
 * students out of comfortable courses into under-enrolled ones until every
 * course hits its minimum, preferring students who actually ranked the course
 * being filled. It is separate because it undoes some of step 3, and the
 * coordinator should be choosing to do that with the numbers in front of them.
 */

export interface PlacementInput {
  termId: string
  courses: NtCourse[]
  students: NtStudent[]
  proposals: NtProposal[]
  priority?: 'first-come' | 'seniors-first' | 'by-rank' | 'lottery'
  overrides?: NtOverride[]
}

const approvedAndScheduled = (courses: NtCourse[]) =>
  courses.filter((c) => c.status === 'approved' && c.block)

export function runPlacement(input: PlacementInput): NtPlacement {
  const { termId, courses, students, proposals } = input
  const priority = input.priority ?? 'seniors-first'
  const live = approvedAndScheduled(courses)
  const byId = new Map(live.map((c) => [c.id, c]))

  const seats: Record<string, number> = {}
  live.forEach((c) => { seats[c.id] = 0 })

  const order = orderStudents(students, priority)
  const rows: NtPlacementRow[] = []
  let firstChoiceCount = 0
  let placedCount = 0
  let unplacedCount = 0

  const approvedProposal = (studentId: string) =>
    proposals.find((p) => p.studentId === studentId && p.status === 'approved')

  // Hand placements, checked against what is actually on offer, and their
  // seats taken up front so the seniors-first walk cannot fill them.
  const valid = (cid: string | undefined, block: NtBlock) =>
    cid && byId.get(cid)?.block === block ? cid : undefined
  const locks = new Map<string, { am?: string; pm?: string }>()
  for (const o of input.overrides ?? []) {
    if (approvedProposal(o.studentId)) continue
    const lock = { am: valid(o.amCourseId, 'AM'), pm: valid(o.pmCourseId, 'PM') }
    if (!lock.am && !lock.pm) continue
    locks.set(o.studentId, lock)
    if (lock.am) seats[lock.am]!++
    if (lock.pm) seats[lock.pm]!++
  }

  /** Where a course sits in this student's own ranking, counted within its block. */
  const choiceOf = (st: NtStudent, cid: string, block: NtBlock): number | null => {
    let n = 0
    for (const r of st.ranks) {
      if (byId.get(r)?.block !== block) continue
      n++
      if (r === cid) return n
    }
    return null
  }

  for (const st of order) {
    const indep = approvedProposal(st.studentId)
    if (indep) {
      rows.push({
        studentId: st.studentId, name: st.name, grade: st.grade,
        independent: true, independentTitle: indep.title,
        amCourseId: null, pmCourseId: null, amChoice: null, pmChoice: null,
      })
      continue
    }

    let am: { id: string; choice: number | null } | null = null
    let pm: { id: string; choice: number | null } | null = null

    // Seats for these were already taken above; just record them.
    const lock = locks.get(st.studentId)
    if (lock?.am) {
      am = { id: lock.am, choice: choiceOf(st, lock.am, 'AM') }; placedCount++
      if (am.choice === 1) firstChoiceCount++
    }
    if (lock?.pm) {
      pm = { id: lock.pm, choice: choiceOf(st, lock.pm, 'PM') }; placedCount++
      if (pm.choice === 1) firstChoiceCount++
    }

    // Choice numbers are counted **within the block**, which is a deliberate
    // change from the prototype. Students rank one combined list, so their top
    // PM course is necessarily at position 2 or later overall — scoring it as a
    // "second choice" made the first-choice rate look permanently bad and told
    // nobody anything true. Here, first AM choice and first PM choice are both
    // first choices, which is what a student means when they say it.
    let seenAm = 0
    let seenPm = 0

    for (const cid of st.ranks) {
      const c = byId.get(cid)
      // Credit recovery is never chosen, only placed by hand.
      if (!c || !c.block || c.assignedOnly) continue
      const isAm = c.block === 'AM'
      if (isAm) seenAm++; else seenPm++
      if (isAm && am) continue
      if (!isAm && pm) continue
      if (seats[cid]! >= c.cap) continue
      if (isAm) {
        am = { id: cid, choice: seenAm }; seats[cid]!++; placedCount++
        if (seenAm === 1) firstChoiceCount++
      } else {
        pm = { id: cid, choice: seenPm }; seats[cid]!++; placedCount++
        if (seenPm === 1) firstChoiceCount++
      }
    }

    // Emptiest course with room, not the first one — spread the unlucky rather
    // than stacking every unplaced student into whatever sorts first.
    const fill = (block: NtBlock) => {
      const open = live
        .filter((c) => c.block === block && !c.assignedOnly && (seats[c.id] ?? 0) < c.cap)
        .sort((x, y) => (seats[x.id] ?? 0) / x.cap - (seats[y.id] ?? 0) / y.cap)
      const c = open[0]
      if (!c) { unplacedCount++; return null }
      seats[c.id] = (seats[c.id] ?? 0) + 1; placedCount++
      return { id: c.id, choice: null as number | null }
    }

    const amFinal = am ?? fill('AM')
    const pmFinal = pm ?? fill('PM')

    rows.push({
      studentId: st.studentId, name: st.name, grade: st.grade,
      amCourseId: amFinal?.id ?? null, pmCourseId: pmFinal?.id ?? null,
      amChoice: amFinal?.choice ?? null, pmChoice: pmFinal?.choice ?? null,
      ...(lock?.am ? { amLocked: true } : {}),
      ...(lock?.pm ? { pmLocked: true } : {}),
    })
  }

  rows.sort((a, b) => a.name.localeCompare(b.name))

  return { termId, rows, seats, firstChoiceCount, placedCount, unplacedCount }
}

function orderStudents(students: NtStudent[], priority: string): NtStudent[] {
  const list = [...students]
  if (priority === 'seniors-first') return list.sort((a, b) => b.grade - a.grade)
  if (priority === 'first-come') {
    // The ranking form's timestamp, earliest first. Anyone without one goes
    // after everyone who has one; ties fall back to student id, so a re-run
    // gives the same answer whatever order the import arrived in.
    const t = (s: NtStudent) => (s.submittedAt ? Date.parse(s.submittedAt) : NaN)
    return list.sort((a, b) => {
      const ta = t(a), tb = t(b)
      if (isNaN(ta) !== isNaN(tb)) return isNaN(ta) ? 1 : -1
      return (isNaN(ta) ? 0 : ta - tb) || a.studentId.localeCompare(b.studentId)
    })
  }
  if (priority === 'lottery') {
    // Deterministic shuffle by student id: a placement run has to be
    // reproducible, or "run it again" quietly becomes "reroll until I like it".
    return list.sort((a, b) => hash(a.studentId) - hash(b.studentId))
  }
  return list
}

const hash = (s: string) => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return h
}

/**
 * Move students into under-enrolled courses until every course clears its
 * minimum, or nothing can move.
 *
 * Preference order for who gets moved: somebody who actually ranked the
 * under-filled course. Failing that, anybody from a course that can spare
 * them. A course below its minimum usually gets cancelled, so this is the step
 * that saves the four-person welding class — at the cost of somebody's second
 * choice, which is why the coordinator triggers it rather than the machine.
 */
export function balanceRosters(
  placement: NtPlacement, courses: NtCourse[], students: NtStudent[],
): { placement: NtPlacement; moved: number } {
  const live = approvedAndScheduled(courses)
  const byId = new Map(live.map((c) => [c.id, c]))
  const ranksOf = new Map(students.map((s) => [s.studentId, s.ranks]))
  const seats = { ...placement.seats }
  const rows = placement.rows.map((r) => ({ ...r }))
  let moved = 0

  for (const block of ['AM', 'PM'] as NtBlock[]) {
    const inBlock = live.filter((c) => c.block === block && !c.assignedOnly)
    // Bounded rather than while(true): a rule change upstream must not be able
    // to hang the coordinator's browser.
    for (let guard = 0; guard < 500; guard++) {
      const under = inBlock
        .filter((c) => seats[c.id]! < c.min)
        .sort((x, y) => (x.min - seats[x.id]!) - (y.min - seats[y.id]!))
        .pop()
      if (!under) break

      const key = block === 'AM' ? 'amCourseId' : 'pmCourseId'
      const choiceKey = block === 'AM' ? 'amChoice' : 'pmChoice'

      const lockKey = block === 'AM' ? 'amLocked' : 'pmLocked'
      const donors = rows.filter((r) => {
        if (r.independent || r[lockKey]) return false
        const from = r[key]
        if (!from || from === under.id) return false
        const c = byId.get(from)
        return !!c && seats[from]! > c.min
      })
      const wanted = donors.filter((r) => ranksOf.get(r.studentId)?.includes(under.id))
      const pick = wanted[0] ?? donors[0]
      if (!pick) break

      seats[pick[key]!]!--
      seats[under.id] = (seats[under.id] ?? 0) + 1
      // Same within-block counting as the main run, so a balanced student's
      // choice number means the same thing as everybody else's.
      const ranks = ranksOf.get(pick.studentId) ?? []
      let n = 0
      let choice: number | null = null
      for (const cid of ranks) {
        const c = byId.get(cid)
        if (!c || c.block !== block) continue
        n++
        if (cid === under.id) { choice = n; break }
      }
      pick[key] = under.id
      pick[choiceKey] = choice
      moved++
    }
  }

  return { placement: { ...placement, rows, seats }, moved }
}

/**
 * Turn a placement into one roster document per course.
 *
 * This is the step that keeps teachers out of the placement document. It runs
 * as part of the same action, because a roster that is one run stale is worse
 * than no roster — the teacher believes it.
 */
export function buildRosters(placement: NtPlacement, courses: NtCourse[]): NtRoster[] {
  const live = approvedAndScheduled(courses)
  return live.map((c) => {
    const key = c.block === 'AM' ? 'amCourseId' : 'pmCourseId'
    return {
      id: `${placement.termId}_${c.id}`,
      termId: placement.termId,
      courseId: c.id,
      teacherUid: c.teacherUid,
      ...(c.teacherEmail ? { teacherEmail: c.teacherEmail.toLowerCase() } : {}),
      block: c.block,
      students: placement.rows
        .filter((r) => r[key] === c.id)
        .map((r) => ({ studentId: r.studentId, name: r.name, grade: r.grade })),
    }
  })
}

/** What the coordinator reads before deciding whether to accept a run. */
export function placementHealth(placement: NtPlacement, courses: NtCourse[]) {
  const live = approvedAndScheduled(courses)
  const under = live.filter((c) => (placement.seats[c.id] ?? 0) < c.min)
  const full = live.filter((c) => (placement.seats[c.id] ?? 0) >= c.cap)
  const assignable = placement.rows.filter((r) => !r.independent).length * 2
  return {
    under,
    full,
    firstChoicePct: assignable ? Math.round((placement.firstChoiceCount / assignable) * 100) : 0,
    unplaced: placement.unplacedCount,
  }
}

/**
 * Apply one hand placement to an existing result — just that student, nobody
 * else moves. What runs when the office places someone by hand, so the change
 * reaches the teacher's roster now rather than at the next placement run (which
 * will keep it there anyway, via the override).
 *
 * A block left undefined keeps whatever the student has. Totals are recounted
 * from the rows rather than adjusted, so they cannot drift.
 */
export function withHandPlacement(
  placement: NtPlacement, courses: NtCourse[], students: NtStudent[], o: NtOverride,
): NtPlacement {
  const live = approvedAndScheduled(courses)
  const byId = new Map(live.map((c) => [c.id, c]))
  const st = students.find((s) => s.studentId === o.studentId)
  const seats = { ...placement.seats }
  const rows = placement.rows.map((r) => {
    if (r.studentId !== o.studentId || r.independent) return r
    const next = { ...r }
    for (const block of ['AM', 'PM'] as NtBlock[]) {
      const want = block === 'AM' ? o.amCourseId : o.pmCourseId
      if (!want || byId.get(want)?.block !== block) continue
      const key = block === 'AM' ? 'amCourseId' : 'pmCourseId'
      const prev = next[key]
      if (prev) seats[prev] = Math.max(0, (seats[prev] ?? 0) - 1)
      seats[want] = (seats[want] ?? 0) + 1
      next[key] = want
      let n = 0
      let choice: number | null = null
      for (const cid of st?.ranks ?? []) {
        if (byId.get(cid)?.block !== block) continue
        n++
        if (cid === want) { choice = n; break }
      }
      if (block === 'AM') { next.amChoice = choice; next.amLocked = true }
      else { next.pmChoice = choice; next.pmLocked = true }
    }
    return next
  })
  const assignable = rows.filter((r) => !r.independent)
  const placedCount = assignable.reduce((n, r) => n + (r.amCourseId ? 1 : 0) + (r.pmCourseId ? 1 : 0), 0)
  return {
    ...placement, rows, seats, placedCount,
    unplacedCount: assignable.length * 2 - placedCount,
    firstChoiceCount: assignable.reduce((n, r) => n + (r.amChoice === 1 ? 1 : 0) + (r.pmChoice === 1 ? 1 : 0), 0),
  }
}

/**
 * One schedule per student from a placement result — what a student can read
 * about their own week, and a teacher about the students in their courses.
 *
 * Keeps each student's artifact choice across re-runs: a student who chose
 * the morning still chose the morning. The choice is dropped only when it can
 * no longer stand — they have no course in that block any more, or they are
 * now on an independent project, which is their artifact.
 */
export function buildSchedules(
  placement: NtPlacement,
  courses: NtCourse[],
  emailOf: Map<string, string>,
  previous: Map<string, { artifactBlock?: NtBlock | null; artifactSetBy?: string }>,
): NtSchedule[] {
  const byId = new Map(courses.map((c) => [c.id, c]))
  return placement.rows.map((r) => {
    const am = r.amCourseId ? byId.get(r.amCourseId) : undefined
    const pm = r.pmCourseId ? byId.get(r.pmCourseId) : undefined
    const prev = previous.get(r.studentId)
    // Credit recovery is not an artifact course: the artifact comes from the
    // other block, so with credit recovery in one block the choice is made.
    const canMake = (b: NtBlock) => (b === 'AM' ? !!am && !am.assignedOnly : !!pm && !pm.assignedOnly)
    const stillValid = !r.independent && prev?.artifactBlock && canMake(prev.artifactBlock)
    const onlyChoice = !r.independent && (am?.assignedOnly || pm?.assignedOnly)
      ? (['AM', 'PM'] as NtBlock[]).find(canMake) ?? null : null
    // Credit recovery all day: no artifact; Friday morning they stay with the
    // morning section, so that is where attendance finds them.
    const allDay = !r.independent && !!am?.assignedOnly && !!pm?.assignedOnly
    const email = emailOf.get(r.studentId)
    return {
      id: `${placement.termId}_${r.studentId}`,
      termId: placement.termId, studentId: r.studentId, name: r.name, grade: r.grade,
      ...(email ? { studentEmail: email } : {}),
      ...(r.independent ? { independent: true, independentTitle: r.independentTitle ?? '' } : {}),
      amCourseId: am?.id ?? null,
      ...(am ? { amTitle: am.title, amTeacherName: am.teacherName, amTeacherUid: am.teacherUid, amRoom: am.room,
        amArtifacts: am.artifactOptions ?? [], ...(am.assignedOnly ? { amCreditRecovery: true } : {}) } : {}),
      pmCourseId: pm?.id ?? null,
      ...(pm ? { pmTitle: pm.title, pmTeacherName: pm.teacherName, pmTeacherUid: pm.teacherUid, pmRoom: pm.room,
        pmArtifacts: pm.artifactOptions ?? [], ...(pm.assignedOnly ? { pmCreditRecovery: true } : {}) } : {}),
      artifactBlock: allDay ? 'AM' : stillValid ? prev!.artifactBlock! : onlyChoice,
      ...(allDay ? { noArtifact: true } : {}),
      ...(stillValid && prev?.artifactSetBy ? { artifactSetBy: prev.artifactSetBy } : {}),
    }
  })
}
